import React, { useCallback, useEffect, useState } from 'react';
import I18N from '@leverege/i18n';
import { Content, Flex, Text, Button, List, CompositeTextInput, Pane } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import debounce from 'lodash.debounce';
import { GlobalState } from '@leverege/ui-redux';
import { isQR, parseQR } from '../../util/QR';
import { isVIN as isValidVIN, ignoreNonVINCharacterEvent, removeNonVINCharacters } from '../../util/VIN';
import S from './DeviceSearch.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('recovr.actions.pairKeys.DeviceSearch');
export default function DeviceSearch(props) {
  const {
    value,
    onClick,
    onChange,
    eventData,
    actions,
    pairing = true,
    titleVariant,
    onClose,
    index,
    locationId,
    deviceId
  } = props;
  const [val, setVal] = useState(value);
  const [items, setItems] = useState([]);
  const isParent = eventData === 'vin';
  const itemName = isParent ? 'Vehicle' : 'Key';
  const createNewItemText = isParent ? t('createNewVehicle') : t('createNewKey');
  const title = isParent ? t('searchVehicle') : t('searchKey');
  const onSearch = useCallback(({
    value
  }) => {
    const newVal = isParent ? removeNonVINCharacters(value?.toUpperCase?.()).slice(0, 17) : value;
    setVal(newVal);
  }, [isParent]);
  const onClear = () => {
    setVal(null);
  };

  // when user clicks button to add new device
  const onAddDevice = useCallback(() => {
    // adjust data for onChange based on vin or keys
    const onChangeData = isParent ? {
      data: {
        field: eventData
      },
      value: val
    } : {
      data: {
        field: eventData,
        index,
        keyId: val
      },
      value: null
    };
    onChange(onChangeData);
    onClick({
      data: {
        screen: 'add',
        field: eventData
      }
    });
  }, [onClick, val, onChange, eventData, index, isParent]);

  // selects one of the items from the results
  const onSelect = useCallback(({
    data
  }) => {
    const selected = isParent ? data.data?.vin : data.data?.keyId;
    const selectedId = data.id;
    // adjust data for onChange based on vin or keys
    const onChangeData = isParent ? {
      data: {
        field: eventData,
        vehicleId: selectedId
      },
      value: selected
    } : {
      data: {
        field: eventData,
        index,
        keyId: selected
      },
      value: data
    };
    onChange(onChangeData);
    onSearch({
      value: null
    });
  }, [eventData, onSearch, index, onChange, isParent]);
  const searchIsValid = val && val.length > 2;
  const debounceParse = debounce(() => {
    if (val && isQR(val)) {
      setVal(parseQR(val));
    }
  }, 500);
  useEffect(debounceParse, [val, debounceParse]);
  useEffect(() => {
    if (searchIsValid) {
      // searches for matching device based on current searchIsValid in the company or location
      const searchForDevice = async () => {
        let res;
        const child = isParent ? 'vehicles' : 'keys';
        try {
          res = await GlobalState.dispatch(actions.parent().parent().item(locationId).child(child).search({
            limit: 100,
            filter: {
              type: 'groupedText',
              value: `*${val}*`
            }
          }));
          const {
            items: rItems
          } = res;
          setItems(rItems);
        } catch (err) {
          console.error('Error searching for valid device', err);
        }
      };
      searchForDevice();
    } else {
      setItems([]);
    }
  }, [val, actions, isParent, locationId, searchIsValid]);
  const onKeyDown = evt => isParent && ignoreNonVINCharacterEvent(evt);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Content.Header, {
      layout: "flex:colL",
      children: [/*#__PURE__*/_jsxs("div", {
        style: {
          position: 'relative'
        },
        children: [/*#__PURE__*/_jsx(TitleBar, {
          variant: titleVariant,
          title: title,
          icon: tIcon('titleIcon')
        }), /*#__PURE__*/_jsx(Button, {
          variant: "pairClose",
          className: S.close,
          onClick: onClose
        })]
      }), /*#__PURE__*/_jsxs(Pane, {
        variant: titleVariant,
        children: [/*#__PURE__*/_jsx(CompositeTextInput, {
          autoFocus: true,
          onFocus: () => {
            setTimeout(() => window?.scrollTo(0, 0), 250);
          },
          pre: /*#__PURE__*/_jsx(Button, {
            variant: "searchBack",
            eventData: {
              screen: 'main',
              field: null
            },
            onClick: onClick
          }),
          post: val && val.length > 0 && /*#__PURE__*/_jsx(Button, {
            variant: "pairClose",
            onClick: onClear
          }),
          variant: "fullWidth",
          value: val,
          onChange: onSearch,
          onKeyDown: onKeyDown,
          changeOnReturn: false,
          alwaysFireOnReturn: false
        }), items && items.length > 0 && /*#__PURE__*/_jsx(Text, {
          variant: "caption",
          className: S.resultCount,
          children: `${items.length} ${t('results')}`
        })]
      })]
    }), /*#__PURE__*/_jsx(Content.Area, {
      className: S.list,
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "colL",
        children: [items && items.length > 0 ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            variant: "subtitle",
            children: t('suggestions')
          }), /*#__PURE__*/_jsx(List, {
            variant: "pairingItem",
            onClick: onSelect,
            children: items.map(item => {
              return /*#__PURE__*/_jsx(ListItem, {
                isParent: isParent,
                deviceId: deviceId,
                item: item
              }, item.id);
            })
          }), isParent && items.length > 1 && /*#__PURE__*/_jsx(Button, {
            variant: "addVehicle",
            onClick: onAddDevice,
            children: createNewItemText
          })]
        }) :
        /*#__PURE__*/
        // eslint-disable-next-line react/jsx-no-useless-fragment
        _jsx(_Fragment, {
          children: searchIsValid ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Text, {
              children: ["The ", itemName, " ", val, " does not exist in the system", isParent ? '.' : ` or is ${pairing ? 'already' : 'not'} paired to a Vehicle.`]
            }), isParent && !isValidVIN(val) && /*#__PURE__*/_jsx(Text, {
              variant: "error",
              children: "This VIN does not follow the standard formatting of 17 characters"
            })]
          }) : /*#__PURE__*/_jsx(Text, {
            children: "Enter at least 3 characters to search."
          })
        }), searchIsValid && items.length === 0 && /*#__PURE__*/_jsx(Button, {
          variant: "addVehicle",
          onClick: onAddDevice,
          children: createNewItemText
        })]
      })
    })]
  });
}
function ListItem(props) {
  const {
    item,
    isParent,
    deviceId,
    ...rest
  } = props;
  let disabled = false;
  // if list of keys, get notes from it and check if its already paired. Disable if so
  let subtitle, searchText, notes;
  if (!isParent) {
    searchText = item.data?.keyId;
    notes = item.data?.notes;
    if (item.data?.vehicle) {
      // forms the subtitle notes if key already paired to vehicle
      const alreadyPaired = item.data?.vehicle.id === deviceId ? t('alreadyPairedSame') : t('alreadyPairedAnother');
      subtitle = notes ? `${notes} - ${alreadyPaired}` : alreadyPaired;
      disabled = true;
    }
  } else {
    searchText = item.data?.vin;
  }
  return /*#__PURE__*/_jsx(List.Item, {
    "data-test-id": `${item.id}`,
    disabled: disabled,
    className: S.suggestion,
    item: item,
    ...rest,
    children: /*#__PURE__*/_jsxs(Flex, {
      variant: "rowM",
      align: "center",
      children: [/*#__PURE__*/_jsx(Text, {
        icon: "@icon:search"
      }), /*#__PURE__*/_jsxs(Flex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(Text, {
          variant: "pairingSuggestion",
          children: searchText
        }), disabled ? /*#__PURE__*/_jsx(Text, {
          variant: "pairingSuggestionSubtitle",
          children: subtitle
        }, subtitle) : /*#__PURE__*/_jsx(Text, {
          variant: "pairingSuggestion",
          children: notes
        }, notes)]
      })]
    })
  });
}